import {merge} from 'lodash';

import {globalWindow} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {CancelError, CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {HttpResponseError, isTransientError} from '../../../libs/shared/core/src/services/http/httpError';
import {LoggerGlobalContext} from '../../../libs/shared/core/src/services/logger/logger';
import {ApiPreBoot} from '../../../src/models/api/bootModel';
import {decodeRawBootDelta, RawBoot} from '../../../src/models/api/rawBootModel';
import {getLocalFeatureFlags} from '../../../src/services/localFeatureFlagReader';
import {
  AppLoadMetricsEnum,
  logAppLoadBootDeltaAge,
  logAppLoadBootWasIncremental,
  logAppLoadMetric,
} from '../performanceMetrics/appLoadMetric';
import {finishBootAsync, registerBootContext} from './bootHelpers';
import {bootLogger} from './bootLogger';
import {maybeReadAndPatchRawBootCacheAsync} from './bootPatchHelpers';
import {BootResultInternal} from './bootTypes';
import {
  FetchBootResult,
  fetchRawBootAsync,
  fetchRawBootInPartsAsync,
  readDataFromBoot,
} from './fetch/bootFetchHelpers';
import {tryDeleteRawBootCache, tryReplaceRawBootCache} from './rawBootCacheHelpers';

/*
 * Constants.
 */

const preBootPath = '/boot/pre/1';

const bootCacheMaxAge = 10 * 24 * 60 * 60 * 1000;

/*
 * API.
 */

const localFeatureFlags = getLocalFeatureFlags();

export async function bootOnlineAsync(
  tryClearPersistedStateAsync: () => Promise<void>,
  onReceivedOnlineBootUserId: (userId: number) => Promise<void>,
): Promise<BootResultInternal> {
  const onlineResult = await beginBootOnlineAsync(tryClearPersistedStateAsync, onReceivedOnlineBootUserId);
  return finishBootOnlineAsync(onlineResult);
}

/*
 * Helpers.
 */

async function beginBootOnlineAsync(
  tryClearPersistedStateAsync: () => Promise<void>,
  onReceivedOnlineBootUserId: (userId: number) => Promise<void>,
): Promise<FetchBootResult> {
  const onOnlineBootFetched = logAppLoadMetric(AppLoadMetricsEnum.ONLINE_BOOT_FETCH);
  try {
    return await fetchBootAsync(onReceivedOnlineBootUserId, {
      // Add the company/user ID to our logging and error reporting as soon as possible.
      onContextFetch: registerBootContext,
    });
  } catch (error) {
    // If we're not authorized, clear persisted data, without potentially swallowing our original error.
    if (error instanceof HttpResponseError && error.status === 401) {
      await tryClearPersistedStateAsync();
    }

    // Pass the error up.
    throw error;
  } finally {
    onOnlineBootFetched();
  }
}

async function fetchBootAsync(
  onReceivedOnlineBootUserId: (userId: number) => Promise<void>,
  options: FetchBootOptions = {},
): Promise<FetchBootResult> {
  // Fetch the raw boot.
  const rawBoot = await fetchRawBootMaybeIncrementallyAsync(onReceivedOnlineBootUserId, options);

  // Cache the raw boot.
  tryReplaceRawBootCache(rawBoot);

  // Decode the raw boot.
  return readDataFromBoot(rawBoot, false);
}

async function finishBootOnlineAsync(onlineResult: FetchBootResult): Promise<BootResultInternal> {
  const {rawBoot} = onlineResult;
  const featureFlags = merge(onlineResult.data.config.launchdarkly.flags, localFeatureFlags);
  const data = await finishBootAsync(onlineResult.data, onlineResult.extraDataGroups);
  return {
    rawBoot,
    initialState: {
      data,
      originalData: data,
      featureFlags: {
        // TODO: This includes LD bootstrap metadata. We should use it to
        // initialize the LD client and then get the flags from there.
        initial: featureFlags,
        dynamic: featureFlags,
      },
    },
  };
}

interface FetchBootOptions {
  /** Called as soon as we know the company/user ID. */
  onContextFetch?: (context: LoggerGlobalContext) => void;
  cancelToken?: CancelToken;
}

export async function fetchRawBootMaybeIncrementallyAsync(
  onReceivedOnlineBootUserId: (userId: number) => Promise<void>,
  options: FetchBootOptions = {},
): Promise<unknown> {
  const {onContextFetch, cancelToken} = options;

  // Call the pre-boot route to determine the company URL and boot-related feature flags.
  const preBoot = await fetchApiAsync(ApiPreBoot, preBootPath, {cancelToken});
  const {companyId, companyUrl, companyRegion, companyCell, userId, userIsVisitor} = preBoot;

  await onReceivedOnlineBootUserId(userId);

  // Provide the company/user ID as soon as possible.
  if (onContextFetch) {
    onContextFetch({
      region: companyRegion,
      cell: companyCell,
      companyId,
      userId,
    });
  }

  // Fetch a visitor boot.
  if (userIsVisitor) {
    return fetchRawBootAsync(companyUrl, {cancelToken});
  }

  // Try to fetch a full boot from the cache, and only a delta boot from the server.
  const rawBoot = await maybeFetchRawBootIncrementallyAsync(companyUrl, cancelToken);
  if (rawBoot) {
    logAppLoadBootWasIncremental();
    return rawBoot;
  }

  // Fetch a full boot in parts.
  return fetchRawBootInPartsAsync(companyUrl, cancelToken);
}

async function maybeFetchRawBootIncrementallyAsync(
  companyUrl: string,
  cancelToken?: CancelToken,
): Promise<RawBoot | undefined> {
  try {
    return await maybeReadAndPatchRawBootCacheAsync(
      globalWindow,
      async (cachedRawBoot) => {
        // As a safeguard, restrict how long we keep patching any given boot.
        const originalTimestamp = cachedRawBoot.client_original_ts ?? cachedRawBoot.ts;
        const originalAge = Date.now() - originalTimestamp;
        if (originalAge > bootCacheMaxAge) {
          return undefined;
        }

        const currentTimestamp = cachedRawBoot.ts;
        const currentAge = Date.now() - currentTimestamp;
        logAppLoadBootDeltaAge(currentAge);

        // Fetch the raw boot delta.
        const deltasSince = cachedRawBoot.ts;
        const refreshToken = cachedRawBoot.refresh_token;
        const rawBootDelta = await fetchRawBootAsync(companyUrl, {
          deltasSince,
          refreshToken,
          cancelToken,
        });

        // Decode the raw boot delta.
        return decodeRawBootDelta(rawBootDelta);
      },
      () => {
        // If we failed to patch a boot, clear the cache.
        tryDeleteRawBootCache();
      },
    );
  } catch (error) {
    if (error instanceof CancelError) {
      throw error;
    }

    // Don't fall back to a full boot just because we're offline.
    if (isTransientError(error)) {
      throw error;
    }

    // The server is telling us to perform a full boot instead.
    if (error instanceof HttpResponseError && error.status === 409) {
      return undefined;
    }

    bootLogger.warn('Incremental boot error', {error: String(error)});
    return undefined;
  }
}
